import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { refreshToken } from "@/services/auth/accountService";

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
	_retry?: boolean;
}

export type Token = {
	accessToken?: string;
	refreshToken?: string;
};

export interface GetService {
	get<T>(url: string, params?: any): Promise<AxiosResponse<T | undefined>>;
}

export interface PostService {
	post<T>(
		url: string,
		data?: any,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse<T | undefined>>;
}

export interface PutService {
	put<T>(
		url: string,
		data?: any,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse<T | undefined>>;
}

type BuildResult<Get extends boolean, Post extends boolean, Put extends boolean> = Get extends true
	? Post extends true
	? Put extends true
	? GetService & PostService & PutService
	: GetService & PostService
	: Put extends true
	? GetService & PutService
	: GetService
	: Post extends true
	? Put extends true
	? PostService & PutService
	: PostService
	: Put extends true
	? PutService
	: never;

export class ApiServiceBuilder<
	Get extends boolean = true,
	Post extends boolean = true,
	Put extends boolean = true
> {
	private constructor(
		private readonly baseURL: string = "",
		private readonly prefix: string = "",
		private readonly version?: string | number,
		private readonly suffix: string = "",
		private readonly isAuthApi: boolean = false,
		private readonly enableGet: boolean = true,
		private readonly enablePost: boolean = true,
		private readonly enablePut: boolean = true
	) { }

	static create(): ApiServiceBuilder {
		return new ApiServiceBuilder();
	}

	withBaseUrl(baseURL: string): ApiServiceBuilder<Get, Post, Put> {
		return new ApiServiceBuilder(
			baseURL,
			this.prefix,
			this.version,
			this.suffix,
			this.isAuthApi,
			this.enableGet,
			this.enablePost,
			this.enablePut
		);
	}

	withVersion(version: string | number): ApiServiceBuilder<Get, Post, Put> {
		return new ApiServiceBuilder(
			this.baseURL,
			this.prefix,
			version,
			this.suffix,
			this.isAuthApi,
			this.enableGet,
			this.enablePost,
			this.enablePut
		);
	}

	withPrefix(prefix: string): ApiServiceBuilder<Get, Post, Put> {
		return new ApiServiceBuilder(
			this.baseURL,
			prefix,
			this.version,
			this.suffix,
			this.isAuthApi,
			this.enableGet,
			this.enablePost,
			this.enablePut
		);
	}

	withSuffix(suffix: string): ApiServiceBuilder<Get, Post, Put> {
		return new ApiServiceBuilder(
			this.baseURL,
			this.prefix,
			this.version,
			suffix,
			this.isAuthApi,
			this.enableGet,
			this.enablePost,
			this.enablePut
		);
	}

	asAuthApi(): ApiServiceBuilder<Get, Post, Put> {
		return new ApiServiceBuilder(
			this.baseURL,
			this.prefix,
			this.version,
			this.suffix,
			true,
			this.enableGet,
			this.enablePost,
			this.enablePut
		);
	}

	withGetMethod(): ApiServiceBuilder<true, false, false> {
		return new ApiServiceBuilder(
			this.baseURL,
			this.prefix,
			this.version,
			this.suffix,
			this.isAuthApi,
			true,
			false,
			false
		);
	}

	withPostMethod(): ApiServiceBuilder<false, true, false> {
		return new ApiServiceBuilder(
			this.baseURL,
			this.prefix,
			this.version,
			this.suffix,
			this.isAuthApi,
			false,
			true,
			false
		);
	}

	withGetAndPostMethods(): ApiServiceBuilder<true, true, false> {
		return new ApiServiceBuilder(
			this.baseURL,
			this.prefix,
			this.version,
			this.suffix,
			this.isAuthApi,
			true,
			true,
			false
		);
	}

	withPutMethod(): ApiServiceBuilder<false, false, true> {
		return new ApiServiceBuilder(
			this.baseURL,
			this.prefix,
			this.version,
			this.suffix,
			this.isAuthApi,
			false,
			false,
			true
		);
	}

	withAllMethods(): ApiServiceBuilder<true, true, true> {
		return new ApiServiceBuilder(
			this.baseURL,
			this.prefix,
			this.version,
			this.suffix,
			this.isAuthApi,
			true,
			true,
			true
		);
	}

	build(): BuildResult<Get, Post, Put> {
		const apiInstance = this.createAxiosInstance(this.baseURL);
		const makeUrl = (url: string) =>
			`${this.prefix}${this.version ? `/v${this.version}` : ""}${this.suffix ?? ""}${url}`;

		const service: Partial<GetService & PostService & PutService> = {};

		if (this.enableGet) {
			service.get = async <T>(url: string, params?: any) => {
				try {
					return await apiInstance.get<T>(makeUrl(url), {
						params,
						paramsSerializer: { indexes: null },
					});
				} catch (error: any) {
					console.error(error);
					return {
						status: error.response?.status ?? 0,
						data: undefined,
					} as AxiosResponse<T | undefined>;
				}
			};
		}

		if (this.enablePost) {
			service.post = async <T>(url: string, data?: any, config?: AxiosRequestConfig) => {
				try {
					return await apiInstance.post<T>(makeUrl(url), data, config);
				} catch (error: any) {
					console.error(error);
					return {
						status: error.response?.status ?? 0,
						data: undefined,
					} as AxiosResponse<T | undefined>;
				}
			};
		}

		if (this.enablePut) {
			service.put = async <T>(url: string, data?: any, config?: AxiosRequestConfig) => {
				try {
					return await apiInstance.put<T>(makeUrl(url), data, config);
				} catch (error: any) {
					console.error(error);
					return {
						status: error.response?.status ?? 0,
						data: undefined,
					} as AxiosResponse<T | undefined>;
				}
			};
		}

		return service as BuildResult<Get, Post, Put>;
	}

	private createAxiosInstance(baseURL: string): AxiosInstance {
		const api = axios.create({ baseURL });
		const isAuthApi = this.isAuthApi;

		api.interceptors.request.use((request) => {
			const token: Token = JSON.parse(localStorage.getItem("tokens") ?? "{}");
			const accessToken = token.accessToken ?? "";

			if (accessToken) {
				request.headers["Authorization"] = `Bearer ${accessToken}`;
			}

			if (isAuthApi) {
				const clientId = process.env.REACT_APP_CLIENT_ID;
				if (clientId) {
					request.headers["X-Client-Id"] = clientId;
				}
			}

			return request;
		});

		api.interceptors.response.use(
			(response) => response,
			async (error) => {
				const originalConfig = error.config as CustomAxiosRequestConfig;
				const token: Token = JSON.parse(localStorage.getItem("tokens") ?? "{}");
				const accessToken = token.accessToken?.replaceAll('"', "") ?? "";
				const refreshTokenStr = token.refreshToken?.replaceAll('"', "") ?? "";

				// Erro de rede/CORS/timeout (sem resposta HTTP): não desloga, apenas propaga.
				if (!error.response) {
					return Promise.reject(error);
				}

				// Já tentou renovar a sessão e falhou de novo: sessão inválida, desloga.
				if (originalConfig._retry) {
					localStorage.removeItem("tokens");
					window.location.href = "/auth";
					return Promise.reject(error);
				}

				const isLoginEndpoint = originalConfig?.url?.includes("/auth/login");
				const isRefreshEndpoint = originalConfig?.url?.includes("/auth/refresh");

				if (
					!isRefreshEndpoint &&
					!isLoginEndpoint &&
					error.response?.status === 401 &&
					!originalConfig._retry &&
					accessToken &&
					refreshTokenStr
				) {
					originalConfig._retry = true;
					try {
						const newTokens = await refreshToken(accessToken, refreshTokenStr);
						if (!newTokens?.accessToken) {
							localStorage.removeItem("tokens");
							window.location.href = "/auth";
							return Promise.reject(error);
						}
						localStorage.setItem("tokens", JSON.stringify(newTokens));
						originalConfig.headers = {
							...(originalConfig.headers || {}),
							Authorization: `Bearer ${newTokens?.accessToken}`,
						};
						return api(originalConfig);
					} catch {
						localStorage.removeItem("tokens");
						window.location.href = "/auth";
					}
				}

				return Promise.reject(error);
			}
		);

		return api;
	}
}
